import { useCallback } from 'react';
import { useRootScope } from 'FrontRoyalAngular';
import { useErrorLogService } from 'ErrorLogging';
import { showToast } from 'Toast';
import { userActionsApi } from 'Users';
import { type PreferenceField } from './editPreferences.types';

type UpdatePreferenceArgs = {
    field: PreferenceField;
    value: string | boolean;
    successContent: string;
    failureContent: string;
    // Extra side-effects to run only after the save succeeds (e.g. language switch,
    // disabling offline mode). Kept out of this hook so the field-specific behavior
    // stays with the row that owns it.
    onSuccess?: () => void;
};

// Persists a single user preference and keeps the global currentUser in sync.
//
// The RTK `updateCurrentUser` mutation does NOT echo generic `pref_*` fields back
// into the global `$rootScope.currentUser` (the server only pushes a hardcoded
// whitelist), so we replicate the old Angular flow of writing the value onto the
// shared currentUser object ourselves — but only after the save succeeds, to avoid
// the locale/currentUser race that https://trello.com/c/pYyc8kX4 fixed.
export function useUpdatePreference() {
    const [updateCurrentUser] = userActionsApi.useUpdateCurrentUserMutation();
    const $rootScope = useRootScope();
    const ErrorLogService = useErrorLogService();

    return useCallback(
        async ({ field, value, successContent, failureContent, onSuccess }: UpdatePreferenceArgs) => {
            const currentUser = $rootScope.currentUser;
            if (!currentUser) return;

            try {
                await updateCurrentUser({ user: { id: currentUser.id, [field]: value } }).unwrap();

                (currentUser as unknown as Record<string, unknown>)[field] = value;
                $rootScope.$applyAsync();

                showToast({ message: successContent, type: 'success' });
                onSuccess?.();
            } catch (error) {
                ErrorLogService.notify('Failed to update user preference', error, { field });
                showToast({ message: failureContent, type: 'danger' });
                throw error;
            }
        },
        [updateCurrentUser, $rootScope, ErrorLogService],
    );
}
